import JSZip from 'jszip'

import { FSContext, FSListResponse, FSRequestParams, IFile } from './types'

/**
 * List directory contents
 * @param context - FS Context (Platform or Team)
 * @param params - Request parameters (path, domain)
 * @returns Promise with code, message, and files array
 */
export async function ls(context: FSContext, params: FSRequestParams): Promise<FSListResponse> {
  const { path, domain } = params
  const api = context.getApiInstance()
  const token = await context.getToken(domain)

  const { code, msg, infos } = await api.get('/vol', {
    params: { path },
    noErrMsg: true,
    domain,
    headers: {
      Authorization: token,
    },
  })

  if (code === 0 && Array.isArray(infos) && infos.length > 0) {
    return {
      code,
      msg,
      files: infos.map(
        (info: {
          name: string
          isDir: boolean
          size: number
          createTime?: { seconds: number }
          modTime?: { seconds: number }
          ext?: string
          updateTime?: number
        }) => {
          const dirname = path === '/' ? '/' : path.endsWith('/') ? path : `${path}/`
          const filePath = dirname === '/' ? `/${info.name}` : `${dirname}${info.name}`
          // Ensure directory paths end with /
          const finalPath = info.isDir && !filePath.endsWith('/') ? `${filePath}/` : filePath

          // Handle different API response formats
          const updateTime =
            info.updateTime || info.modTime?.seconds || info.createTime?.seconds || 0

          // Generate unique ID
          const id = info.createTime?.seconds
            ? btoa(encodeURIComponent(`${info.name}-${info.size}-${info.createTime.seconds}`))
            : finalPath

          let ext: string | undefined
          if (!info.isDir) {
            const fComponents = info.name?.split('.')
            if (fComponents?.length && fComponents.length > 1) {
              ext = fComponents[fComponents.length - 1].toLowerCase()
            }
          }

          return {
            id,
            key: finalPath,
            name: info.name,
            isDir: info.isDir,
            size: info.size,
            dirname,
            path: finalPath,
            ext,
            updateTime,
          }
        },
      ),
    }
  }

  return { code, msg, files: [] }
}

/**
 * Move or rename file/folder
 * @param context - FS Context
 * @param params - Source path, target path, and domain
 */
export async function mv(
  context: FSContext,
  params: { path: string; target: string; domain: number },
): Promise<void> {
  const { path, target, domain } = params
  const api = context.getApiInstance()
  const token = await context.getToken(domain)

  return new Promise<void>((resolve, reject) => {
    api
      .patch(
        '/vol',
        { path, target },
        {
          headers: {
            Authorization: token,
          },
          params: { path, target },
          noErrMsg: true,
          domain,
        },
      )
      .then(({ code }: { code: number }) => {
        if (code === 0) {
          resolve()
        } else {
          reject()
        }
      })
      .catch((e: any) => {
        reject(e)
      })
  })
}

/**
 * Rename file or folder (convenience wrapper for mv)
 * @param context - FS Context
 * @param params - Path, new name, and domain
 */
export async function rename(
  context: FSContext,
  params: { path: string; name: string; domain: number },
): Promise<void> {
  const { path, name, domain } = params
  const api = context.getApiInstance()
  const token = await context.getToken(domain)

  return new Promise<void>((resolve, reject) => {
    api
      .patch(
        '/vol',
        { path, name },
        {
          headers: {
            Authorization: token,
          },
          params: { path, name },
          noErrMsg: true,
          domain,
        },
      )
      .then(({ code }: { code: number }) => {
        if (code === 0) {
          resolve()
        } else {
          reject()
        }
      })
      .catch((e: any) => {
        reject(e)
      })
  })
}

/**
 * Delete file or folder
 * @param context - FS Context
 * @param params - Path and domain
 */
export async function rm(
  context: FSContext,
  params: { path: string; domain: number },
): Promise<void> {
  const { path, domain } = params
  const api = context.getApiInstance()
  const token = await context.getToken(domain)

  return new Promise<void>((resolve, reject) => {
    api
      .delete('/vol', {
        headers: {
          Authorization: token,
        },
        params: { path },
        noErrMsg: true,
        domain,
      })
      .then(({ code }: { code: number }) => {
        if (code === 0) {
          resolve()
        } else {
          reject()
        }
      })
      .catch((e: any) => {
        reject(e)
      })
  })
}

/**
 * Create a new folder
 * @param context - FS Context
 * @param params - Path (must end with /) and domain
 */
export async function mkdir(
  context: FSContext,
  params: { path: string; domain: number },
): Promise<void> {
  const { path, domain } = params
  const api = context.getApiInstance()
  const token = await context.getToken(domain)

  return new Promise<void>((resolve, reject) => {
    api
      .post('/vol', undefined, {
        headers: {
          Authorization: token,
        },
        params: { path },
        domain,
      })
      .then(() => {
        resolve()
      })
      .catch((e: any) => {
        reject(e)
      })
  })
}

/**
 * Download a single file
 * @param context - FS Context
 * @param params - File path and domain
 */
export async function download(
  context: FSContext,
  params: { path: string; domain: number },
): Promise<void> {
  const { path, domain } = params
  const a = document.createElement('a')
  a.download = path.split('/').reverse()[0]
  const token = await context.getToken(domain)
  const baseURL = context.getBaseURL(domain)

  if (!baseURL) {
    throw new Error('Base URL not found for domain')
  }

  a.href = `${baseURL}/vol?path=${encodeURIComponent(path)}&Authorization=${encodeURIComponent(
    token,
  )}`
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()

  // Delay removal to ensure browser starts download
  await new Promise((resolve) => setTimeout(resolve, 100))
  a.remove()
}

/**
 * Download multiple files as a ZIP archive
 * @param context - FS Context
 * @param params - Files array and domain
 */
export async function downloadAsZip(
  context: FSContext,
  params: { files: IFile[]; domain: number },
): Promise<void> {
  const { files, domain } = params

  if (files.length === 0) {
    throw new Error('No files to download')
  }

  const zip = new JSZip()
  const token = await context.getToken(domain)
  const baseURL = context.getBaseURL(domain)

  if (!baseURL) {
    throw new Error('Base URL not found for domain')
  }

  // Download all files and add them to the ZIP
  const downloadPromises = files.map(async (file) => {
    try {
      const url = `${baseURL}/vol?path=${encodeURIComponent(file.path)}&Authorization=${encodeURIComponent(token)}`
      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`Failed to download ${file.name}`)
      }

      const blob = await response.blob()
      zip.file(file.name, blob)
    } catch (error) {
      console.error(`Error downloading file ${file.name}:`, error)
      throw error
    }
  })

  // Wait for all files to be downloaded
  await Promise.all(downloadPromises)

  // Generate ZIP file
  const zipBlob = await zip.generateAsync({ type: 'blob' })

  // Create download link
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
  const zipFilename = `files-${timestamp}.zip`

  const a = document.createElement('a')
  a.href = URL.createObjectURL(zipBlob)
  a.download = zipFilename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()

  // Cleanup
  await new Promise((resolve) => setTimeout(resolve, 100))
  URL.revokeObjectURL(a.href)
  a.remove()
}

/**
 * Progress callback type for download operations
 */
export type DownloadProgressCallback = (loaded: number, total: number) => void

/**
 * Download a single file with progress tracking
 * @param context - FS Context
 * @param params - File path, domain, and optional abort controller
 * @param onProgress - Progress callback
 */
export async function downloadWithProgress(
  context: FSContext,
  params: { path: string; domain: number; abortController?: AbortController },
  onProgress?: DownloadProgressCallback,
): Promise<void> {
  const { path, domain, abortController } = params
  const token = await context.getToken(domain)
  const baseURL = context.getBaseURL(domain)

  if (!baseURL) {
    throw new Error('Base URL not found for domain')
  }

  const url = `${baseURL}/vol?path=${encodeURIComponent(path)}&Authorization=${encodeURIComponent(token)}`
  const response = await fetch(url, {
    signal: abortController?.signal,
  })

  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.statusText}`)
  }

  const contentLength = response.headers.get('content-length')
  const total = contentLength ? parseInt(contentLength, 10) : 0
  const contentType = response.headers.get('content-type') || 'application/octet-stream'

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('Response body is not readable')
  }

  const chunks: BlobPart[] = []
  let loaded = 0
  let done = false

  while (!done) {
    const result = await reader.read()
    done = result.done

    if (result.value) {
      chunks.push(result.value)
      loaded += result.value.length
      onProgress?.(loaded, total)
    }
  }

  const blob = new Blob(chunks, { type: contentType })
  const filename = path.split('/').reverse()[0]

  // Trigger download
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()

  await new Promise((resolve) => setTimeout(resolve, 100))
  URL.revokeObjectURL(a.href)
  a.remove()
}

/**
 * Download multiple files as a ZIP archive with progress tracking
 * @param context - FS Context
 * @param params - Files array, domain, and optional abort controller
 * @param onProgress - Progress callback (loaded bytes, total bytes)
 */
export async function downloadAsZipWithProgress(
  context: FSContext,
  params: { files: IFile[]; domain: number; abortController?: AbortController },
  onProgress?: DownloadProgressCallback,
): Promise<void> {
  const { files, domain, abortController } = params

  if (files.length === 0) {
    throw new Error('No files to download')
  }

  const zip = new JSZip()
  const token = await context.getToken(domain)
  const baseURL = context.getBaseURL(domain)

  if (!baseURL) {
    throw new Error('Base URL not found for domain')
  }

  // Calculate total size (sum of all file sizes)
  const totalSize = files.reduce((acc, f) => acc + (f.size || 0), 0)
  let totalLoaded = 0

  // Download files sequentially to track progress accurately
  for (const file of files) {
    if (abortController?.signal.aborted) {
      throw new Error('Download cancelled')
    }

    const url = `${baseURL}/vol?path=${encodeURIComponent(file.path)}&Authorization=${encodeURIComponent(token)}`
    const response = await fetch(url, {
      signal: abortController?.signal,
    })

    if (!response.ok) {
      throw new Error(`Failed to download ${file.name}`)
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error(`Response body is not readable for ${file.name}`)
    }

    const chunks: BlobPart[] = []
    let done = false

    while (!done) {
      const result = await reader.read()
      done = result.done

      if (result.value) {
        chunks.push(result.value)
        totalLoaded += result.value.length
        onProgress?.(totalLoaded, totalSize)
      }
    }

    const blob = new Blob(chunks)
    zip.file(file.name, blob)
  }

  // Generate ZIP file
  const zipBlob = await zip.generateAsync({ type: 'blob' })

  // Create download link
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
  const zipFilename = `files-${timestamp}.zip`

  const a = document.createElement('a')
  a.href = URL.createObjectURL(zipBlob)
  a.download = zipFilename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()

  await new Promise((resolve) => setTimeout(resolve, 100))
  URL.revokeObjectURL(a.href)
  a.remove()
}

/**
 * Trigger volume usage refresh
 * @param context - FS Context
 * @param params - Domain only
 */
export async function refresh(context: FSContext, params: { domain: number }): Promise<void> {
  const { domain } = params
  const api = context.getApiInstance()
  const token = await context.getToken(domain)

  try {
    await api.post('/vol/refresh', undefined, {
      headers: { Authorization: token },
      noErrMsg: true,
      domain,
    })
  } catch (e) {
    // Silent failure - refresh is best-effort
    console.warn('Volume refresh failed:', e)
  }
}

/**
 * Validate file/folder name
 * @param filename - Name to validate
 * @returns true if valid, false otherwise
 */
export function validFileName(filename: string): boolean {
  if (filename === '.' || filename === '..') return false
  const filenameReg = /^[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FA5A-Za-z0-9_\-\\.]+$/g
  return filenameReg.test(filename)
}
